<script setup>
// "On this page" table of contents (Figma node 75-2014). A heading plus a list
// of in-page anchor links; the section currently in view is highlighted (pink
// pill + chevron), tracked with an IntersectionObserver scroll-spy. Layout-
// agnostic — the consuming view supplies any sticky wrapper and column width.
//
// SSG-safe: the observer is only created in onMounted (browser-only), so the
// prerender never touches IntersectionObserver.
import { ref, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  // [{ id, label }] — id matches the target section's id in the view.
  items: { type: Array, required: true },
  title: { type: String, default: 'On this page' },
  // Optional CTA panel below the links (Figma "CTA Section"). Unused on
  // /our-approach; reserved so the box can be dropped in later.
  cta: { type: Object, default: null },   // { heading, body, to, label }
})

const activeId = ref(props.items[0]?.id ?? null)

const isExternal = (to) => typeof to === 'string' && /^https?:\/\//.test(to)

let observer = null
const visible = new Set()

onMounted(() => {
  const targets = props.items
    .map((it) => document.getElementById(it.id))
    .filter(Boolean)
  if (!targets.length || typeof IntersectionObserver === 'undefined') return

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target.id)
        else visible.delete(entry.target.id)
      }
      // Highlight the first item (in document order) currently crossing the
      // centre band; fall back to the last active if nothing is in view.
      const first = props.items.find((it) => visible.has(it.id))
      if (first) activeId.value = first.id
    },
    // Thin band across the viewport middle — a section is "active" while its
    // content sits over the centre of the screen.
    { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
  )
  targets.forEach((el) => observer.observe(el))
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})

function onClick(event, id) {
  const el = typeof document !== 'undefined' ? document.getElementById(id) : null
  if (!el) return
  event.preventDefault()
  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  activeId.value = id
  if (typeof history !== 'undefined') history.replaceState(null, '', `#${id}`)
}
</script>

<template>
  <nav class="page-toc" :aria-label="title">
    <p class="toc-title">{{ title }}</p>

    <ul class="links">
      <li v-for="item in items" :key="item.id">
        <a
          :href="`#${item.id}`"
          class="link"
          :class="{ active: item.id === activeId }"
          :aria-current="item.id === activeId ? 'true' : undefined"
          @click="onClick($event, item.id)"
        >
          <span class="label">{{ item.label }}</span>
          <svg
            class="chevron"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M7 4l6 6-6 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </a>
      </li>
    </ul>

    <div v-if="cta" class="cta">
      <p v-if="cta.heading" class="cta-title">{{ cta.heading }}</p>
      <p v-if="cta.body" class="cta-body">{{ cta.body }}</p>
      <component
        :is="isExternal(cta.to) ? 'a' : 'RouterLink'"
        v-if="cta.to && cta.label"
        v-bind="isExternal(cta.to)
          ? { href: cta.to, target: '_blank', rel: 'noopener noreferrer' }
          : { to: cta.to }"
        class="btn primary sm"
      >
        {{ cta.label }}
      </component>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
.page-toc {
  .toc-title {
    margin: 0 0 $space-4;
    font-size: $font-size-h4;
    line-height: $lh-heading-sm;
    letter-spacing: $ls-heading;
    font-weight: 500;
    color: var(--color-ink);
  }

  .links {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .link {
    display: flex;
    align-items: center;
    gap: $space-4;
    padding: $space-3 $space-4;
    @include rounded($radius);
    text-decoration: none;
    color: var(--color-text);
    font-size: $font-size-lg;
    line-height: $lh-base;
    @include transition(background-color, color);

    .label { flex: 1; min-width: 0; }

    .chevron {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      color: var(--color-primary);
      opacity: 0;
      @include transition(opacity);
    }

    &:hover { background: var(--color-fill); }
    &:focus-visible { @include focus-ring; outline: none; }

    // Current section — darkened-cream pill, ink text (Figma active state;
    // colour swapped from pink to the existing --color-bg-alt cream tone —
    // pink read as too bold for this small in-page highlight). The chevron
    // is always rendered now (see template) and only its opacity toggles
    // here, so its 20px width + the row's $space-4 gap stay reserved in
    // layout at all times — the label never reflows when a link activates.
    &.active {
      background: var(--color-bg-alt);
      color: var(--color-ink);
      .chevron { opacity: 1; }
    }
  }

  .cta {
    margin-top: $space-6;
    padding: $space-6;
    background: var(--color-fill);
    @include rounded($radius);

    .cta-title {
      margin: 0 0 $space-3;
      font-size: $font-size-h4;
      font-weight: 500;
      color: var(--color-ink);
    }

    .cta-body {
      margin: 0 0 $space-5;
      color: var(--color-text);
    }
  }
}
</style>
